import React, { useState } from 'react';
import {
  Menu, X, Phone, Mail, MapPin, Star, Shield, Clock,
  Leaf, Scissors, Sprout, Trees, TreePine, CheckCircle,
  ArrowRight, Send, Facebook,
} from 'lucide-react';

export default function AboveBeyondWebsite() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');

  // ============================================================
  // ============ CUSTOMIZE YOUR CONTENT HERE ===================
  // ============================================================

  // ---- Identity ----
  const companyName = "Above & Beyond";
  const companyNameFull = "Above & Beyond Lawn Care";
  const tagline = "Your Home. Our Priority.";
  const valueProposition =
    "We take pride in delivering reliable, high-quality services that truly go above and beyond expectations. Locally owned and serving the Quad Cities.";

  // ---- Contact ----
  const phoneDisplay = "(563) 676-6958";
  const phoneTel = "+15636766958"; // used for tel: links
  const contactEmail = "info@aboveandbeyondqc.com";
  const facebookUrl = ""; // optional — leave "" to hide the icon

  // ---- Service Area ----
  const serviceArea = ["Davenport", "Bettendorf", "Le Claire", "Eldridge"];

  // ---- Hours ----
  const hours = "Mon – Sat, by appointment. Call to schedule.";

  // ---- Web3Forms ----
  // Get a free access key at https://web3forms.com (no signup required — just enter your email).
  // Paste it below. Until you do, the form will fail silently with a "TODO" toast.
  const web3formsKey = "dff5e7d9-442e-43da-8cad-ef12a920ae8f";

  // ---- Services ----
  const services = [
    {
      icon: Scissors,
      title: "Mowing & Edging",
      short: "Clean cuts and crisp edges, every visit.",
      long: "Weekly or bi-weekly mowing on a schedule that suits your lawn. Edges trimmed cleanly along walkways, drives, and beds — with the signature stripes neighbors notice.",
    },
    {
      icon: Sprout,
      title: "Trimming",
      short: "Detail work where the mower can't reach.",
      long: "String trimming around fences, foundations, posts, mailboxes, and obstacles. The finishing touch that makes a yard look properly cared for instead of just cut.",
    },
    {
      icon: Leaf,
      title: "Leaf Cleanup",
      short: "Spring and fall cleanups, done right.",
      long: "Comprehensive leaf removal in fall and bed cleanouts in spring. We bag, blow, and haul so your lawn is ready for the next season — not buried under last one's debris.",
    },
    {
      icon: Trees,
      title: "Hedge Trimming",
      short: "Shaped, healthy hedges and shrubs.",
      long: "Precision trimming and shaping for hedges, shrubs, and ornamental bushes. We keep growth in check and the lines clean, framing your home the way it should be.",
    },
    {
      icon: TreePine,
      title: "Mulching",
      short: "Fresh mulch that protects and elevates.",
      long: "New mulch installation or refresh — hardwood, dyed, or natural. Suppresses weeds, locks in moisture, and instantly lifts the curb appeal of every bed it touches.",
    },
  ];

  // ---- Why Choose Us ----
  const credibility = [
    {
      icon: Shield,
      title: "Locally Owned",
      text: "A family-run business right here in the Quad Cities. You're hiring a neighbor, not a national chain.",
    },
    {
      icon: Star,
      title: "Quality You Can See",
      text: "Every yard treated like our own. Straight stripes, sharp edges, and details that make the difference.",
    },
    {
      icon: Clock,
      title: "Reliable & On Time",
      text: "We show up when we say we will. No rescheduling games, no missed weeks during the busy season.",
    },
    {
      icon: CheckCircle,
      title: "Free, Honest Quotes",
      text: "Quick pricing with no pressure and no surprises. Pick up the phone or fill out the form — we'll handle the rest.",
    },
  ];

  // ============================================================
  // ============ END CUSTOMIZATION SECTION =====================
  // ============================================================

  const navigate = (page) => {
    setCurrentPage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ---------- Reusable: Brand Wordmark ----------
  const Wordmark = ({ size = 'md' }) => {
    const sizes = {
      sm: { main: 'text-xl', sub: 'text-[9px] tracking-[0.2em]' },
      md: { main: 'text-2xl', sub: 'text-[10px] tracking-[0.22em]' },
      lg: { main: 'text-4xl', sub: 'text-xs tracking-[0.25em]' },
    };
    const s = sizes[size];
    return (
      <div className="flex flex-col leading-none">
        <span className={`font-display font-bold text-red-600 ${s.main}`}>
          Above <span className="text-red-700">✝</span> Beyond
        </span>
        <span className={`font-sans font-semibold text-stone-700 mt-1 ${s.sub}`}>
          LAWN CARE
        </span>
      </div>
    );
  };

  // ---------- Navigation ----------
  const Navigation = () => {
    const navItems = [
      { id: 'home', label: 'Home' },
      { id: 'services', label: 'Services' },
      { id: 'about', label: 'About' },
      { id: 'contact', label: 'Contact' },
    ];
    return (
      <nav className="bg-white/95 backdrop-blur-sm border-b border-stone-200 fixed w-full top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <button onClick={() => navigate('home')} className="hover:opacity-80 transition">
              <Wordmark size="md" />
            </button>

            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigate(item.id)}
                  className={`text-sm font-semibold tracking-wide transition ${
                    currentPage === item.id
                      ? 'text-red-600'
                      : 'text-stone-700 hover:text-red-600'
                  }`}
                >
                  {item.label.toUpperCase()}
                </button>
              ))}
              <a
                href={`tel:${phoneTel}`}
                className="bg-red-600 text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-red-700 transition shadow-sm flex items-center gap-2"
              >
                <Phone size={16} /> {phoneDisplay}
              </a>
            </div>

            <button
              className="md:hidden text-stone-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-stone-200">
            <div className="px-4 py-4 space-y-3">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigate(item.id)}
                  className="block w-full text-left text-stone-700 hover:text-red-600 font-semibold py-2"
                >
                  {item.label}
                </button>
              ))}
              <a
                href={`tel:${phoneTel}`}
                className="block bg-red-600 text-white px-4 py-3 rounded-md font-semibold text-center mt-2"
              >
                Call {phoneDisplay}
              </a>
            </div>
          </div>
        )}
      </nav>
    );
  };

  // ---------- Reusable: Section Heading ----------
  const SectionHeading = ({ eyebrow, title, subtitle, center = false }) => (
    <div className={`mb-12 ${center ? 'text-center max-w-3xl mx-auto' : 'max-w-3xl'}`}>
      {eyebrow && (
        <div className="text-red-600 font-semibold tracking-[0.2em] text-xs uppercase mb-3">
          {eyebrow}
        </div>
      )}
      <h2 className="font-display text-4xl md:text-5xl font-bold text-stone-900 mb-4">
        {title}
      </h2>
      {subtitle && <p className="text-lg text-stone-600 leading-relaxed">{subtitle}</p>}
    </div>
  );

  // ---------- HOME PAGE ----------
  const HomePage = () => (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-screen flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/lawn-house-wide.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/80 via-stone-900/60 to-stone-900/30" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="max-w-2xl">
            <div className="text-red-400 font-semibold tracking-[0.25em] text-sm uppercase mb-4">
              Quad Cities Lawn Care
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold text-white mb-6 leading-[1.05]">
              {tagline}
            </h1>
            <p className="text-xl text-stone-100 mb-10 leading-relaxed max-w-xl">
              {valueProposition}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${phoneTel}`}
                className="bg-red-600 text-white px-8 py-4 rounded-md text-lg font-semibold hover:bg-red-700 transition shadow-xl flex items-center justify-center gap-2"
              >
                <Phone size={20} /> Call {phoneDisplay}
              </a>
              <button
                onClick={() => navigate('contact')}
                className="bg-white text-stone-900 px-8 py-4 rounded-md text-lg font-semibold hover:bg-stone-100 transition shadow-xl flex items-center justify-center gap-2"
              >
                Request a Free Quote <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="What We Do"
            title="Full-Service Lawn Care"
            subtitle="From weekly mowing to seasonal cleanups, we handle the work that keeps your home's first impression sharp."
            center
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <div
                  key={idx}
                  className="p-8 bg-stone-50 rounded-lg border border-stone-200 hover:border-red-200 hover:shadow-lg transition group"
                >
                  <div className="w-12 h-12 bg-red-600 rounded-md flex items-center justify-center mb-5 group-hover:scale-110 transition">
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-stone-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-stone-600 leading-relaxed">{service.short}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <button
              onClick={() => navigate('services')}
              className="inline-flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 transition"
            >
              See all services <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="Why Choose Us"
            title="The Difference Is in the Details"
            center
          />
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {credibility.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-12 h-12 bg-white rounded-md flex items-center justify-center shadow-sm border border-stone-200">
                    <Icon className="text-red-600" size={22} />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-bold text-stone-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-stone-600 leading-relaxed">{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Showcase Photo */}
      <section className="relative h-[500px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/lawn-stripes-closeup.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-4 h-full flex items-end pb-16">
          <div className="max-w-xl">
            <h3 className="font-display text-4xl md:text-5xl font-bold text-white mb-3">
              Stripes that turn heads.
            </h3>
            <p className="text-stone-100 text-lg">
              The kind of lawn the neighbors notice — and ask who you use.
            </p>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-red-600 font-semibold tracking-[0.2em] text-xs uppercase mb-3">
            <MapPin size={14} /> Service Area
          </div>
          <h2 className="font-display text-4xl font-bold text-stone-900 mb-6">
            Proudly Serving the Quad Cities
          </h2>
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {serviceArea.map((city) => (
              <span
                key={city}
                className="bg-stone-100 text-stone-800 px-5 py-2 rounded-full font-semibold border border-stone-200"
              >
                {city}
              </span>
            ))}
          </div>
          <p className="text-stone-600">
            Not sure if you're in our area? Give us a call — we may be able to help.
          </p>
        </div>
      </section>

      {/* CTA */}
      <CTABand />
    </>
  );

  // ---------- Reusable CTA ----------
  const CTABand = () => (
    <section className="py-20 px-4 bg-red-600">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
          Ready for a yard you'll be proud of?
        </h2>
        <p className="text-red-100 text-lg mb-8">
          Free quotes. No pressure. Call today or send a message.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href={`tel:${phoneTel}`}
            className="bg-white text-red-600 px-8 py-4 rounded-md text-lg font-semibold hover:bg-stone-100 transition shadow-lg flex items-center justify-center gap-2"
          >
            <Phone size={20} /> {phoneDisplay}
          </a>
          <button
            onClick={() => navigate('contact')}
            className="bg-stone-900 text-white px-8 py-4 rounded-md text-lg font-semibold hover:bg-stone-800 transition shadow-lg flex items-center justify-center gap-2"
          >
            Request a Quote <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );

  // ---------- SERVICES PAGE ----------
  const ServicesPage = () => (
    <>
      <section className="pt-32 pb-16 px-4 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="Services"
            title="Everything Your Yard Needs"
            subtitle="One trusted crew for the work that keeps your home looking its best — from the first cut of spring to the last leaf of fall."
          />
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto space-y-10">
          {services.map((service, idx) => {
            const Icon = service.icon;
            return (
              <div
                key={idx}
                className="flex flex-col md:flex-row gap-6 p-8 bg-stone-50 rounded-lg border border-stone-200"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-red-600 rounded-md flex items-center justify-center">
                    <Icon className="text-white" size={28} />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-stone-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-stone-600 leading-relaxed text-lg">{service.long}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing note */}
      <section className="py-20 px-4 bg-stone-50">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-red-600 font-semibold tracking-[0.2em] text-xs uppercase mb-3">
            Pricing
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mb-4">
            Every Yard Is Different
          </h2>
          <p className="text-lg text-stone-600 leading-relaxed mb-8">
            Lot size, terrain, frequency, what's already growing — it all factors in. That's
            why we don't post one-size-fits-all prices. Call for a free, no-pressure quote
            tailored to your property.
          </p>
          <a
            href={`tel:${phoneTel}`}
            className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-4 rounded-md text-lg font-semibold hover:bg-red-700 transition shadow-lg"
          >
            <Phone size={20} /> Call {phoneDisplay}
          </a>
        </div>
      </section>

      <CTABand />
    </>
  );

  // ---------- ABOUT PAGE ----------
  const AboutPage = () => (
    <>
      <section className="pt-32 pb-16 px-4 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="About"
            title="Built on Care, Quality, and Showing Up"
            subtitle={tagline + " That's not just a slogan — it's how we approach every property we serve."}
          />
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="font-display text-3xl font-bold text-stone-900 mb-5">
              A neighbor, not a national chain.
            </h3>
            <p className="text-stone-600 leading-relaxed mb-4 text-lg">
              {companyNameFull} was founded on a simple idea: do the work the right way, treat
              every customer like family, and earn business one yard at a time.
            </p>
            <p className="text-stone-600 leading-relaxed mb-4 text-lg">
              We're locally owned and operated — based in the Quad Cities and serving the
              homes around us. When you call, you talk to the people doing the work. When we
              show up, we treat your property like it's our own.
            </p>
            <p className="text-stone-600 leading-relaxed text-lg">
              No subcontractors. No bait-and-switch pricing. Just honest, reliable lawn care
              from people who take pride in what they do.
            </p>
          </div>
          <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
            <img
              src="/lawn-stripes-closeup.jpg"
              alt="Detailed view of a freshly mowed and striped lawn"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-4 bg-stone-900">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-red-400 font-semibold tracking-[0.2em] text-xs uppercase mb-3">
              What We Stand For
            </div>
            <h2 className="font-display text-4xl font-bold text-white mb-4">
              Our Values
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <h4 className="font-display text-xl font-bold text-white mb-3">Integrity</h4>
              <p className="text-stone-300 leading-relaxed">
                We do what we say, and we do it right — even when no one's watching.
              </p>
            </div>
            <div className="text-center">
              <h4 className="font-display text-xl font-bold text-white mb-3">Quality</h4>
              <p className="text-stone-300 leading-relaxed">
                Anyone can mow a lawn. We sweat the details that turn a yard into a showpiece.
              </p>
            </div>
            <div className="text-center">
              <h4 className="font-display text-xl font-bold text-white mb-3">Community</h4>
              <p className="text-stone-300 leading-relaxed">
                These are our neighbors' homes. We work to make the whole neighborhood better.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Brand image */}
      <section className="py-20 px-4 bg-stone-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-red-600 font-semibold tracking-[0.2em] text-xs uppercase mb-3">
            Our Brand
          </div>
          <h2 className="font-display text-3xl font-bold text-stone-900 mb-8">
            Look for the red lawn-care crew.
          </h2>
          <div className="inline-block bg-stone-900 p-8 rounded-lg shadow-xl">
            <img
              src="/logo-shirt.png"
              alt="Above & Beyond Lawn Care logo"
              className="max-w-md w-full h-auto"
            />
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );

  // ---------- CONTACT PAGE ----------
  const ContactPage = () => {
    const [formStatus, setFormStatus] = useState('idle'); // 'idle' | 'sending' | 'success' | 'error'
    const [formData, setFormData] = useState({
      name: '',
      phone: '',
      email: '',
      address: '',
      service: '',
      message: '',
    });

    const handleChange = (e) => {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setFormStatus('sending');

      const payload = new FormData();
      payload.append('access_key', web3formsKey);
      payload.append('subject', `New quote request from ${formData.name}`);
      payload.append('from_name', companyNameFull);
      Object.entries(formData).forEach(([k, v]) => payload.append(k, v));

      try {
        const res = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          body: payload,
        });
        const data = await res.json();
        if (data.success) {
          setFormStatus('success');
          setFormData({ name: '', phone: '', email: '', address: '', service: '', message: '' });
        } else {
          setFormStatus('error');
        }
      } catch {
        setFormStatus('error');
      }
    };

    return (
      <>
        <section className="pt-32 pb-16 px-4 bg-stone-50">
          <div className="max-w-6xl mx-auto">
            <SectionHeading
              eyebrow="Get In Touch"
              title="Request a Free Quote"
              subtitle="Tell us a bit about your yard and we'll get back to you with a no-pressure quote. Prefer to talk? Call us anytime — we'd rather hear your voice."
            />
          </div>
        </section>

        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-12">
            {/* Form */}
            <div className="md:col-span-3">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">
                    Property Address or City *
                  </label>
                  <input
                    type="text"
                    name="address"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="e.g. 1234 Main St, Bettendorf"
                    className="w-full px-4 py-3 border border-stone-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">
                    Service Needed
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition bg-white"
                  >
                    <option value="">Select a service…</option>
                    {services.map((s) => (
                      <option key={s.title} value={s.title}>
                        {s.title}
                      </option>
                    ))}
                    <option value="Multiple Services">Multiple Services</option>
                    <option value="Not Sure">Not Sure / Need a Recommendation</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">
                    Tell us about your yard
                  </label>
                  <textarea
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Lot size, current condition, anything special we should know…"
                    className="w-full px-4 py-3 border border-stone-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full bg-red-600 text-white px-8 py-4 rounded-md text-lg font-semibold hover:bg-red-700 transition shadow-lg flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {formStatus === 'sending' ? (
                    'Sending…'
                  ) : (
                    <>
                      Send Request <Send size={18} />
                    </>
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 text-green-800 rounded-md">
                    Thanks — we got it! We'll be in touch soon. For urgent requests please call{' '}
                    <a href={`tel:${phoneTel}`} className="font-bold underline">
                      {phoneDisplay}
                    </a>.
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-md">
                    Something went wrong sending the form. Please call{' '}
                    <a href={`tel:${phoneTel}`} className="font-bold underline">
                      {phoneDisplay}
                    </a>{' '}
                    instead.
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info */}
            <div className="md:col-span-2 space-y-8">
              <div className="p-8 bg-stone-900 rounded-lg text-white">
                <h3 className="font-display text-2xl font-bold mb-6">Reach Us Directly</h3>
                <div className="space-y-5">
                  <a
                    href={`tel:${phoneTel}`}
                    className="flex items-start gap-4 hover:text-red-400 transition group"
                  >
                    <div className="w-10 h-10 bg-red-600 rounded-md flex items-center justify-center flex-shrink-0 group-hover:bg-red-700 transition">
                      <Phone size={18} />
                    </div>
                    <div>
                      <div className="text-sm text-stone-400 uppercase tracking-wider mb-1">Phone</div>
                      <div className="text-xl font-semibold">{phoneDisplay}</div>
                    </div>
                  </a>
                  <a
                    href={`mailto:${contactEmail}`}
                    className="flex items-start gap-4 hover:text-red-400 transition group"
                  >
                    <div className="w-10 h-10 bg-red-600 rounded-md flex items-center justify-center flex-shrink-0 group-hover:bg-red-700 transition">
                      <Mail size={18} />
                    </div>
                    <div>
                      <div className="text-sm text-stone-400 uppercase tracking-wider mb-1">Email</div>
                      <div className="text-base font-semibold break-all">{contactEmail}</div>
                    </div>
                  </a>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-red-600 rounded-md flex items-center justify-center flex-shrink-0">
                      <Clock size={18} />
                    </div>
                    <div>
                      <div className="text-sm text-stone-400 uppercase tracking-wider mb-1">Hours</div>
                      <div className="text-base">{hours}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8 bg-stone-50 rounded-lg border border-stone-200">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin size={20} className="text-red-600" />
                  <h3 className="font-display text-xl font-bold text-stone-900">
                    Service Area
                  </h3>
                </div>
                <ul className="space-y-2 text-stone-700">
                  {serviceArea.map((city) => (
                    <li key={city} className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-red-600 flex-shrink-0" />
                      {city}, IA
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  };

  // ---------- Footer ----------
  const Footer = () => (
    <footer className="bg-stone-950 text-stone-400 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-8 mb-8">
          <div>
            <Wordmark size="md" />
            <p className="mt-4 text-sm max-w-sm leading-relaxed">
              Locally owned lawn care serving the Quad Cities. Mowing, trimming, cleanups, and more.
            </p>
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <a href={`tel:${phoneTel}`} className="hover:text-white transition flex items-center gap-2">
              <Phone size={14} /> {phoneDisplay}
            </a>
            <a href={`mailto:${contactEmail}`} className="hover:text-white transition flex items-center gap-2">
              <Mail size={14} /> {contactEmail}
            </a>
            {facebookUrl && (
              <a href={facebookUrl} target="_blank" rel="noreferrer" className="hover:text-white transition flex items-center gap-2">
                <Facebook size={14} /> Facebook
              </a>
            )}
          </div>
        </div>
        <div className="pt-8 border-t border-stone-800 text-xs text-stone-500 flex flex-col sm:flex-row justify-between gap-2">
          <div>© {new Date().getFullYear()} {companyNameFull}. All rights reserved.</div>
          <div>{serviceArea.join(' · ')}</div>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="min-h-screen bg-white text-stone-900">
      <Navigation />
      {currentPage === 'home' && <HomePage />}
      {currentPage === 'services' && <ServicesPage />}
      {currentPage === 'about' && <AboutPage />}
      {currentPage === 'contact' && <ContactPage />}
      <Footer />
    </div>
  );
}
